import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from 'react-router-dom';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
  FormHelperText,
  alpha,
  useTheme,
  Autocomplete,
  Chip,
} from "@mui/material";
import axios from "axios";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { BugReport as BugReportIcon, Add as AddIcon } from '@mui/icons-material';

// Styled components
const PageWrap = styled(Box)(() => ({
  width: '100%',
  maxWidth: 760,
  margin: '0 auto',
  padding: '8px 4px',
}));

const HeaderCard = styled(Box)(() => ({
  marginBottom: 28,
  borderBottom: '1px solid var(--artemy-border)',
  paddingBottom: 16,
}));

const FormCard = styled(Box)(() => ({
  background: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: 14,
  padding: '28px 28px 24px',
  '&:hover': { borderColor: 'var(--artemy-border-bright)' },
  transition: 'border-color 0.2s',
}));

const AField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)', boxShadow: '0 0 0 2px rgba(61,127,255,0.12)' },
    '& input, & textarea': { color: 'var(--artemy-text-primary)', fontSize: 13 },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-secondary)', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  '& .MuiFormHelperText-root': { color: 'var(--artemy-text-dim)', fontSize: 11 },
}));

const ASelect = styled(FormControl)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: 'var(--artemy-bg-elevated)',
    color: 'var(--artemy-text-primary)',
    fontSize: 13,
    '& fieldset': { borderColor: 'var(--artemy-border)' },
    '&:hover fieldset': { borderColor: 'var(--artemy-border-bright)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--artemy-accent)' },
    '& .MuiSelect-icon': { color: 'var(--artemy-text-secondary)' },
  },
  '& .MuiInputLabel-root': { color: 'var(--artemy-text-secondary)', fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: 'var(--artemy-accent)' },
  '& .MuiFormHelperText-root': { color: 'var(--artemy-text-dim)', fontSize: 11 },
}));

const SubmitBtn = styled(Button)(() => ({
  borderRadius: 8,
  padding: '11px 24px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 14,
  fontFamily: 'var(--artemy-font-body)',
  background: 'var(--artemy-accent)',
  color: '#fff',
  '&:hover': { background: '#3570e5', boxShadow: '0 4px 14px rgba(61,127,255,0.35)' },
  '&:disabled': { background: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-dim)' },
  transition: 'all 0.18s',
}));

const StyledAlert = styled(Alert)({
  marginBottom: '1rem',
  borderRadius: 8,
  fontSize: 13,
});

const TaskForm = ({ collapsed }) => {
  const theme = useTheme();
  const location = useLocation();
  const history = useHistory();
  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector(state => state.auth);

  const { Id: podId, podname } = location.state || {};
  const [pods, setPods] = useState([]);
  const [selectedPodId, setSelectedPodId] = useState(podId || '');
  const [selectedPodName, setSelectedPodName] = useState(podname || '');
  const [podMembers, setPodMembers] = useState([]);
  const [slackUsers, setSlackUsers] = useState([]);
  const [userStories, setUserStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    podTaskName: "",
    description: "",
    dueDate: "",
    taskOwner: "",
    category: "",
    status: "",
    jiraItems: [],
    notificationRecipient: "",
    hasBlocker: false
  });

  const fetchPods = async () => {
    if (!user?.data?.[0]?.emp_id || !token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_GETMULTIPLECLIENTPODS,
        { clientID: user.data[0].emp_id },
        { headers: { Authorization: token } }
      );
      if (response.data?.data?.podDetails) setPods(response.data.data.podDetails);
    } catch (err) {
      setError(intl.formatMessage({ id: "podsFetchError" }));
    }
  };

  const fetchPodMembers = async () => {
    if (!selectedPodId || !token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_CLIENTFETCHPODMEMBERS,
        { pod_Id: selectedPodId },
        { headers: { Authorization: token } }
      );
      if (response.data?.data) setPodMembers(response.data.data);
    } catch (err) {
      setError(intl.formatMessage({ id: "podMembersFetchError" }));
    }
  };

  const fetchJiraIssues = async () => {
    if (!token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_JIRAISSUESFILTERMANAGER,
        { manager_id: user.data[0].emp_id, pod_id: [] },
        { headers: { Authorization: token } }
      );
      if (response.data.statusCode === 200 && response.data.body) {
        setUserStories(response.data.body.jiraIssues || []);
      }
    } catch (err) {
      console.warn("JIRA fetch error:", err.message);
    }
  };

  const fetchSlackUsers = async () => {
    if (!token) return;
    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_SLACKUSERSFETCH,
        { token: process.env.REACT_APP_SLACK_TOKEN },
        { headers: { Authorization: token } }
      );
      if (response.data?.body?.users) setSlackUsers(response.data.body.users);
    } catch (err) {
      console.error("Error fetching Slack users:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(
        process.env.REACT_APP_CLIENT_CLIENTCREATEPODTASK,
        {
          project_key: selectedPodName,
          pod_task_name: formData.podTaskName,
          description: formData.description,
          due_date: formData.dueDate,
          candidate_id: parseInt(formData.taskOwner),
          category: formData.category,
          status: formData.status,
          pod_id: selectedPodId,
          issue_key: formData.jiraItems,
          blockercheck: formData.hasBlocker ? 1 : 0
        },
        { headers: { Authorization: token } }
      );
      if (formData.notificationRecipient) {
        await axios.post(
          process.env.REACT_APP_CLIENT_SLACKINDIVIDUALTASKNOTIFICATION,
          { token: process.env.REACT_APP_SLACK_TOKEN, user_id: formData.notificationRecipient, task_name: formData.podTaskName },
          { headers: { Authorization: token } }
        );
      }
      setSuccess(intl.formatMessage({ id: "taskCreatedSuccess" }));
      setTimeout(() => history.push(`/ViewPodTasksManagerbyid`), 2000);
    } catch (err) {
      setError(intl.formatMessage({ id: "taskCreationError" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPods(); fetchSlackUsers(); fetchJiraIssues(); }, [token, user]);
  useEffect(() => {
    if (selectedPodId) { fetchPodMembers(); setFormData(prev => ({ ...prev, taskOwner: "" })); }
  }, [selectedPodId, token]);

  const getCharacterCount = (field, limit) => `${formData[field].length}/${limit}`;
  const isPodSelected = Boolean(selectedPodId);

  return (
    <PageWrap>
      {/* Header */}
      <HeaderCard>
        <Typography sx={{ fontFamily: 'var(--artemy-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--artemy-text-primary)', letterSpacing: '-0.4px' }}>
          {intl.formatMessage({ id: "createTask" })}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)', mt: 0.5 }}>
          Create and assign a task to a pod member
        </Typography>
      </HeaderCard>

      {error && <StyledAlert severity="error">{error}</StyledAlert>}
      {success && <StyledAlert severity="success">{success}</StyledAlert>}
      {!isPodSelected && (
        <StyledAlert severity="info">Select a Pod to enable all fields</StyledAlert>
      )}

      <FormCard component="form" onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          {/* Pod Selection */}
          <ASelect fullWidth required>
            <InputLabel>{intl.formatMessage({ id: "selectPod" })}</InputLabel>
            <Select
              value={selectedPodId}
              onChange={e => {
                const pod = pods.find(p => p.id === e.target.value);
                setSelectedPodId(e.target.value);
                setSelectedPodName(pod?.podName || '');
              }}
              label={intl.formatMessage({ id: "selectPod" })}
            >
              {pods.map(pod => <MenuItem key={pod.id} value={pod.id}>{pod.podName}</MenuItem>)}
            </Select>
          </ASelect>

          {/* Task Name */}
          <AField
            required fullWidth
            label={intl.formatMessage({ id: "podTaskName" })}
            value={formData.podTaskName}
            onChange={e => setFormData(prev => ({ ...prev, podTaskName: e.target.value }))}
            inputProps={{ maxLength: 50 }}
            disabled={!isPodSelected}
            helperText={getCharacterCount('podTaskName', 50)}
          />

          {/* Description */}
          <AField
            required fullWidth multiline rows={3}
            label={intl.formatMessage({ id: "podTaskDescription" })}
            value={formData.description}
            onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
            inputProps={{ maxLength: 100 }}
            disabled={!isPodSelected}
            helperText={getCharacterCount('description', 100)}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            {/* Due Date */}
            <AField
              required fullWidth type="date"
              label={intl.formatMessage({ id: "podTaskDueDate" })}
              value={formData.dueDate}
              onChange={e => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              disabled={!isPodSelected}
            />

            {/* Task Owner */}
            <ASelect fullWidth required>
              <InputLabel>{intl.formatMessage({ id: "podTaskOwner" })}</InputLabel>
              <Select
                value={formData.taskOwner}
                onChange={e => setFormData(prev => ({ ...prev, taskOwner: e.target.value }))}
                label={intl.formatMessage({ id: "podTaskOwner" })}
                disabled={!isPodSelected}
              >
                {podMembers.map(member => <MenuItem key={member.c_id} value={member.c_id}>{member.firstName}</MenuItem>)}
              </Select>
              <FormHelperText>{!isPodSelected ? "Select a Pod first" : ""}</FormHelperText>
            </ASelect>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            {/* Status */}
            <ASelect fullWidth required>
              <InputLabel>{intl.formatMessage({ id: "podTaskStatus" })}</InputLabel>
              <Select
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                label={intl.formatMessage({ id: "podTaskStatus" })}
                disabled={!isPodSelected}
              >
                <MenuItem value="To Do">To Do</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Done">Done</MenuItem>
              </Select>
            </ASelect>

            {/* Category */}
            <ASelect fullWidth required>
              <InputLabel>{intl.formatMessage({ id: "podCategories" })}</InputLabel>
              <Select
                value={formData.category}
                onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                label={intl.formatMessage({ id: "podCategories" })}
                disabled={!isPodSelected}
              >
                <MenuItem value="Design">Design</MenuItem>
                <MenuItem value="Coding">Coding</MenuItem>
                <MenuItem value="Testing">Testing</MenuItem>
                <MenuItem value="Documentation">Documentation</MenuItem>
              </Select>
            </ASelect>
          </Box>

          {/* JIRA Issues */}
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={userStories}
              getOptionLabel={option => typeof option === 'string' ? option : option.issue_key}
              value={formData.jiraItems.map(item => userStories.find(issue => issue.issue_key === item) || item)}
              onChange={(event, newValue) => {
                setFormData(prev => ({ ...prev, jiraItems: newValue.map(v => typeof v === 'string' ? v : v.issue_key) }));
              }}
              filterOptions={(options, { inputValue }) =>
                options.filter(option =>
                  option.issue_key.toLowerCase().includes(inputValue.toLowerCase()) ||
                  (option.issue_summary && option.issue_summary.toLowerCase().includes(inputValue.toLowerCase()))
                )
              }
              renderInput={params => (
                <AField
                  {...params}
                  label="JIRA Issues (optional)"
                  placeholder="Search JIRA issues..."
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props} sx={{ py: 1, px: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BugReportIcon sx={{ color: 'var(--artemy-accent)', fontSize: 16 }} />
                      <Typography variant="body2" sx={{ color: 'var(--artemy-text-primary)', fontWeight: 500, fontSize: 13 }}>
                        {option.issue_key}
                      </Typography>
                    </Box>
                    {option.issue_summary && (
                      <Typography variant="body2" sx={{ ml: 3.5, mt: 0.3, color: 'var(--artemy-text-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {option.issue_summary}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.issue_key || index}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <BugReportIcon sx={{ fontSize: 13 }} />
                        {option.issue_key}
                      </Box>
                    }
                    sx={{
                      m: 0.3, height: 26, borderRadius: 1,
                      background: 'rgba(61,127,255,0.15)',
                      color: 'var(--artemy-accent)',
                      border: '1px solid rgba(61,127,255,0.3)',
                      fontSize: 12,
                      '& .MuiChip-deleteIcon': { color: 'var(--artemy-accent)', '&:hover': { color: '#fff' } },
                    }}
                  />
                ))
              }
            />
          </FormControl>

          {/* Slack Notification */}
          <ASelect fullWidth>
            <InputLabel>{intl.formatMessage({ id: "notificationRecipient" })}</InputLabel>
            <Select
              value={formData.notificationRecipient}
              onChange={e => setFormData(prev => ({ ...prev, notificationRecipient: e.target.value }))}
              label={intl.formatMessage({ id: "notificationRecipient" })}
              disabled={!isPodSelected}
            >
              {slackUsers.map(u => <MenuItem key={u.user_id} value={u.user_id}>{u.name}</MenuItem>)}
            </Select>
            <FormHelperText>Optional: Notify via Slack</FormHelperText>
          </ASelect>

          {/* Blocker */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hasBlocker}
                onChange={e => setFormData(prev => ({ ...prev, hasBlocker: e.target.checked }))}
                disabled={!isPodSelected}
                sx={{ color: 'var(--artemy-text-dim)', '&.Mui-checked': { color: 'var(--artemy-accent)' } }}
              />
            }
            label={<Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)' }}>{intl.formatMessage({ id: "checkBoxBlocker" })}</Typography>}
          />

          {/* Submit */}
          <Box sx={{ pt: 1 }}>
            <SubmitBtn
              type="submit"
              variant="contained"
              disabled={loading || !isPodSelected || !formData.podTaskName || !formData.taskOwner}
              fullWidth
            >
              <AddIcon sx={{ fontSize: 18, mr: 0.8 }} />
              {loading ? intl.formatMessage({ id: "submitting" }) : intl.formatMessage({ id: "submit" })}
            </SubmitBtn>
          </Box>
        </Stack>
      </FormCard>
    </PageWrap>
  );
};

export default TaskForm;